// Workspace access policy admin client (PB-4826).
//
//   GET /api/auth/admin/access-policy  → the three controls + the revision
//   PUT /api/auth/admin/access-policy  → the whole policy + the revision that was read
//
// Mirrors services/auth/src/admin/dto/access-policy.response.ts. The endpoints
// ship with PB-2926; this file is the read/write surface they were missing.
//
// NOT in docs/api/auth.yaml. That file is a Collaboration consumed-boundary input
// hashed byte-exact, so PB-2926 could not document these two paths and recorded the
// divergence in services/auth/test/contract/openapi.spec.ts instead. The controller
// and the response DTO are the contract until that transition lands.

import { AdminApiError, request } from "./admin-users-api.js";

/** The policy as the server reports it: the three controls plus the concurrency
    token and the provenance the console needs to say "defaults" versus "chosen". */
export interface AccessPolicy {
  localAccountsEnabled: boolean;
  localInvitationsEnabled: boolean;
  invitationLifetimeHours: number;
  revision: number;
  /** Null while no HUMAN has saved the policy — the shipped defaults, or the
      migration's backfill, are what is on screen. */
  updatedAt: string | null;
  updatedByUserId: string | null;
}

/** The editable half. `revision` is deliberately absent: it is not policy data,
    it is the precondition, so it travels beside the policy and can never be
    mistaken for a field the administrator chose. */
export interface AccessPolicyDraft {
  localAccountsEnabled: boolean;
  localInvitationsEnabled: boolean;
  invitationLifetimeHours: number;
}

export { AdminApiError };

/* Mirrors the CHECK in 202608150002_tenant_access_policy and the controller's own
   bounds. Duplicated rather than derived for the same reason the controller
   duplicates them: the database stays authoritative, and this layer exists so a
   bad value is refused before a round trip instead of coming back as a 400. */
export const INVITE_LIFETIME_MIN = 24;
export const INVITE_LIFETIME_MAX = 168;
export const INVITE_LIFETIME_STEP = 24;

/**
 * The code `AccessPolicyService.update` throws when the presented revision is
 * stale.
 *
 * Matched on the CODE, never on an HTTP status: `request()` reads `{code, message}`
 * off the error envelope and discards `response.status`, so `409` is not
 * observable here. Auth's exception filter preserves a thrown body's own `code`
 * (PB-540), which is what makes this string reliable.
 */
export const POLICY_CONFLICT_CODE = "policy_revision_conflict";

/**
 * Tenant-scoped, for the same reason every other admin key on this page is: two
 * administrators of different tenants can occupy one mounted `/admin` tree across
 * a `/me` refetch, and an unscoped key would serve one tenant's security policy to
 * the other's administrator (the PB-1556 class).
 *
 * One key, no filters, so it is also the join point for the three surfaces that
 * read this policy — the panel, the invite dialog and the create-account button
 * share a single fetch rather than making three.
 */
export function accessPolicyQueryKey(
  tenantId: string | null | undefined,
): readonly unknown[] {
  return ["auth", "admin", "access-policy", tenantId ?? "none"] as const;
}

export async function getAccessPolicy(): Promise<AccessPolicy> {
  return (await request("/api/auth/admin/access-policy")) as AccessPolicy;
}

/**
 * Replace the policy.
 *
 * The whole policy every time, as the endpoint requires — it rejects unknown keys
 * and a PATCH surface would let two administrators' saves interleave into a
 * combination neither of them reviewed. `revision` is the one the caller READ.
 */
export async function updateAccessPolicy(
  policy: AccessPolicyDraft,
  revision: number,
): Promise<AccessPolicy> {
  return (await request("/api/auth/admin/access-policy", {
    method: "PUT",
    body: JSON.stringify({ ...policy, revision }),
  })) as AccessPolicy;
}

/** Whether a failed save lost the optimistic-concurrency race. Exported so the
    panel narrows in one place and a future caller cannot reinvent it against the
    status code that is not there. */
export function isPolicyRevisionConflict(error: unknown): boolean {
  return error instanceof AdminApiError && error.code === POLICY_CONFLICT_CODE;
}

/** Client-side pre-check mirroring the controller's `requireInteger`. Rejects a
    fractional value rather than rounding it: a policy that silently turned 1.5
    into 1 would report a lifetime the administrator did not choose. */
export function validateInvitationLifetime(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return "Choose an invitation lifetime.";
  if (!/^\d+$/.test(trimmed)) return "Choose a whole number of days.";
  const hours = Number(trimmed);
  if (
    hours < INVITE_LIFETIME_MIN ||
    hours > INVITE_LIFETIME_MAX ||
    hours % INVITE_LIFETIME_STEP !== 0
  ) {
    return "Choose between 1 and 7 whole days.";
  }
  return null;
}
